/**
 * LES TIROIRS — le corps, le sac, la carte, le journal, le pupitre (design §3).
 *
 * IL REND UN FRAGMENT, PAS UN CADRE : les tirettes doivent se ranger dans la MÊME rangée que la
 * sortie du livre, et un conteneur intermédiaire en aurait fait deux rangées sur un téléphone. Le
 * cadre appartient donc à la tranche, qui est seule à savoir combien de tirettes elle porte.
 *
 * TOUT CE QUI N'EST PAS LE TEXTE SE RANGE. Au lot 3, ces quatre panneaux vivaient dépliés dans une
 * colonne de droite : sur mobile ils passaient SOUS le texte, si loin qu'ils n'existaient plus, et
 * sur grand écran la carte ouverte repoussait les choix hors de l'écran. Ils sont maintenant cinq
 * tirettes et cinq tiroirs — fermés, ils ne coûtent rien ; ouverts, ils prennent franchement la
 * place, le temps qu'on les consulte.
 *
 * L'ORDRE N'EST PAS DÉCORATIF. Le corps d'abord (manger, se reposer : ce qui tue le plus souvent
 * dans une aventure longue n'est pas un monstre, c'est la faim), puis le sac, puis la carte, puis
 * le journal — qui est, plus que la carte, ce qui sauve une reprise après trois semaines (§12) —,
 * et le pupitre en dernier, qui ne sert pas à jouer.
 *
 * AUCUN TIROIR NE S'OUVRE TOUT SEUL. Pas de butin qui surgit, pas d'alerte qui interrompt : ce qui
 * doit être su s'écrit dans la page.
 */
import { useState } from "react";

import type {
  Caracteristique,
  CarteEnVue,
  HerosEnVue,
  HexagoneFrequente,
  LesAnnotations,
  LigneDeJournal,
  Marqueur,
  ObjetConnu,
  PartieEnVue,
  QueteAuCarnet
} from "../../api/types.js";
import { Glyphe, type NomDeGlyphe } from "../../composants/Glyphe.js";
import { Popin } from "../../composants/Popin.js";
import { BlocDeCaracteristiques, Equipement } from "../FicheDuHeros.js";
import {
  BLESSURES,
  CARACTERISTIQUES,
  EMPLACEMENTS,
  RARETES,
  REPOS,
  ditLeConsommable,
  ditLeParchemin,
  dureeDite,
  estSurcharge,
  nomDObjet,
  poidsDit,
  verbeDeConsommation
} from "../../lexique/mots.js";
import { LaCarteAnnotee } from "./CarteAnnotee.js";
import { LePupitre } from "./Pupitre.js";
import { ditLaLigne } from "../../lexique/journal.js";
import { t, tn } from "../../lexique/langue.js";

type Tiroir = "corps" | "sac" | "carte" | "journal" | "pupitre";

/** L'ORDRE N'EST PAS DÉCORATIF (voir l'en-tête). Le pupitre vient EN DERNIER : c'est le seul qui ne
 * serve pas à jouer — on y règle la lampe et la taille du texte, une fois, et l'on n'y revient
 * plus. Le mettre avant le corps, c'est mettre le réglage avant la faim. */
const TIRETTES: { clef: Tiroir; nom: string; glyphe: NomDeGlyphe; couleur?: string }[] = [
  { clef: "corps", nom: "Le corps", glyphe: "vie", couleur: "var(--sang)" },
  { clef: "sac", nom: "Le sac", glyphe: "sac" },
  { clef: "carte", nom: "La carte", glyphe: "carte" },
  { clef: "journal", nom: "Le journal", glyphe: "plume" },
  { clef: "pupitre", nom: "Le pupitre", glyphe: "lampe" }
];

/** CE QUE CHAQUE REPOS COÛTE EN RATIONS. Recopié du moteur, et c'est le seul endroit où on le
 * recopie : il ne sert qu'à griser un bouton et à dire pourquoi. Le refus, lui, reste au serveur —
 * un front qui autoriserait par erreur se ferait dire non, et c'est le bon sens de la marche. */
const RATIONS_DU_REPOS: Record<string, number> = { souffle: 0, halte: 1, long: 2, camp: 2, sanctuaire: 1 };

/** L'emplacement où poser un objet : les anneaux ont deux doigts, on prend le premier libre. */
function clefPour(objet: ObjetConnu, heros: HerosEnVue): string | null {
  if (!objet.emplacement) return null;
  if (objet.emplacement !== "anneau") return objet.emplacement;
  return heros.equipement.anneau_1 ? "anneau_2" : "anneau_1";
}

/**
 * LE CARNET DE QUÊTES (§4.6) — au-dessus du carnet de bord, dans le même tiroir.
 *
 * Le journal répond à « qu'est-ce qui s'est passé » ; les quêtes répondent à « qu'est-ce que je fais
 * ici ». C'est la même question posée par les deux bouts, et un sixième tiroir pour la seconde
 * moitié aurait fait une barre de navigation là où il y en a déjà une.
 *
 * TOUT CE QUI EST ICI EST UNE CITATION DE L'AVENTURE (§13.1) : titres et résumés ont été écrits dans
 * sa langue et s'affichent tels quels. Ce qui se traduit est le cadre — « en cours », « il reste ».
 *
 * ET CE QU'ON N'AFFICHE PAS N'EST PAS DÉCIDÉ ICI : le serveur n'envoie ni le titre des étapes à
 * venir ni ce qu'elles exigent (§4.6). Un composant qui filtrerait serait un composant dont l'onglet
 * réseau ferait mentir le filtre.
 */
function LeCarnetDeQuetes({ quetes }: { quetes: QueteAuCarnet[] }) {
  if (!quetes.length) return null;
  return (
    <>
      <div className="tiroir__soustitre capitales">{t("Ce que vous poursuivez")}</div>
      <ul className="quetes">
        {quetes.map((quete) => (
          <li key={quete.id} className={quete.achevee ? "quete quete--achevee" : "quete"}>
            <span className="rang rang--espace">
              <span className="capitales">{quete.titre}</span>
              {quete.principale ? <span className="faible">{t("la vôtre")}</span> : null}
            </span>
            <p className="faible quete__resume">{quete.resume}</p>

            <ol className="quete__etapes">
              {quete.faites.map((etape) => (
                <li key={etape.id} className="quete__etape quete__etape--faite">
                  <Glyphe nom="suite" taille={12} />
                  <span>{etape.titre}</span>
                </li>
              ))}
              {quete.enCours ? (
                <li className="quete__etape quete__etape--encours">
                  <Glyphe nom="plume" taille={12} />
                  <span>{quete.enCours.titre}</span>
                </li>
              ) : null}
            </ol>

            {/* COMBIEN IL EN RESTE, ET PAS LESQUELLES. Leur titre dirait où aller, c'est-à-dire la
                solution ; leur nombre dit seulement la longueur du chemin. */}
            {quete.restantes > 0 ? (
              <p className="faible quete__reste">
                {tn(quete.restantes, "Il reste {n} étape après celle-là.", "Il reste {n} étapes après celle-là.")}
              </p>
            ) : null}
            {quete.achevee ? <p className="faible quete__reste">{t("Menée à son terme.")}</p> : null}
          </li>
        ))}
      </ul>
    </>
  );
}

export function LesTiroirs({
  partie,
  catalogue,
  carte,
  journal,
  annotations,
  frequentation = [],
  bloque,
  surRepas,
  surRepos,
  surUtilise,
  surLit,
  surSoin,
  surAllume,
  surEteint,
  surEquipe,
  surJette,
  surAnnote,
  surResout,
  surPublie,
  surEfface,
  surVote
}: {
  partie: PartieEnVue;
  catalogue: Map<string, ObjetConnu>;
  carte: CarteEnVue | null;
  journal: LigneDeJournal[];
  annotations: LesAnnotations;
  /** Le calque des morts (§4.4), pour la carte. */
  frequentation?: HexagoneFrequente[];
  bloque: boolean;
  surRepas: () => void;
  surRepos: (type: string) => void;
  surUtilise: (objetId: string) => void;
  /** LIRE UN PARCHEMIN (§3.7). `blessure` n'est donné que pour le rebouteux : c'est le joueur qui
   * désigne ce qu'on lui referme, et se tromper de blessure coûterait le parchemin. */
  surLit: (objetId: string, blessure?: string) => void;
  /** LE REBOUTEUX D'UN SANCTUAIRE (§3.4) : une blessure contre de l'or. */
  surSoin: (blessure: string) => void;
  /** ALLUMER ET ÉTEINDRE (§3.8). Deux gestes plutôt qu'une bascule : on allume QUELQUE CHOSE — on
   * peut porter une torche et une lanterne —, et l'on éteint ce qui brûle. */
  surAllume: (objetId: string) => void;
  surEteint: () => void;
  surEquipe: (objetId: string | null, clef: string, retire: boolean) => void;
  surJette: (objetId: string, quantite: number) => void;
  surAnnote: (hexagone: string, marqueur: Marqueur, note: string, publique: boolean) => void;
  surResout: (id: string, resolue: boolean) => void;
  surPublie: (id: string, publique: boolean) => void;
  surEfface: (id: string) => void;
  surVote: (id: string, pour: boolean) => void;
}) {
  const [ouvert, poseLOuvert] = useState<Tiroir | null>(null);
  const ferme = () => poseLOuvert(null);

  const { heros } = partie;
  const enCombat = partie.combat !== null;
  const rations = heros.sac.find((p) => p.objetId === "ration")?.quantite ?? 0;
  const kits = heros.sac.find((p) => p.objetId === "kit_camp")?.quantite ?? 0;
  /** UN SANCTUAIRE (§3.4). Les deux gestes qu'il ouvre — le repos qui soigne, le rebouteux —
   * n'existent nulle part ailleurs, et l'on n'affiche donc rien de tout cela ailleurs : un bouton
   * désaturé qu'on ne pourra jamais presser n'apprend rien, il encombre. */
  const auSanctuaire = partie.noeud?.sanctuaire === true;

  return (
    <>
      {TIRETTES.map((tirette) => {
        // Le compte de ce qu'il y a dedans, quand il décide de quelque chose : deux rations, ce
        // n'est pas la même partie que douze. Sur la carte, ce sont les affaires en suspens (§4.2),
        // et c'est le seul chiffre qui appelle à l'ouvrir.
        const compte =
          tirette.clef === "sac"
            ? heros.sac.length
            : tirette.clef === "journal"
              ? journal.length
              : tirette.clef === "carte"
                ? annotations.miennes.filter((a) => !a.resolue).length || null
                : null;
        return (
          <button
            key={tirette.clef}
            type="button"
            className="tirette"
            onClick={() => poseLOuvert(tirette.clef)}
            aria-haspopup="dialog"
          >
            <Glyphe nom={tirette.glyphe} taille={16} couleur={tirette.couleur} />
            <span className="tirette__nom capitales">{t(tirette.nom)}</span>
            {compte !== null ? <span className="nombre faible">{compte}</span> : null}
          </button>
        );
      })}

      <Popin
        ouvert={ouvert === "corps"}
        titre={t("Le corps")}
        glyphe={<Glyphe nom="vie" taille={16} couleur="var(--sang)" />}
        surFermeture={ferme}
      >
        <BlocDeCaracteristiques heros={heros} />

        <div className="rang rang--espace" style={{ marginTop: "var(--e4)" }}>
          <span className="capitales">{t("Rations")}</span>
          <span className="nombre">{rations}</span>
        </div>

        {/* CE QUI BRÛLE (§3.8), et pour combien de temps. C'est le chiffre qui décide si l'on entre
            dans la galerie maintenant ou si l'on fait demi-tour — il a donc sa ligne, à côté des
            rations, et non un coin d'écran. */}
        {heros.lumiere ? (
          <div className="rang rang--espace">
            <span className="capitales">{heros.lumiere.nom}</span>
            <span className="rang rang--serre">
              <span className="nombre">{t("{heures} h", { heures: heros.lumiere.heuresRestantes })}</span>
              <button type="button" className="puce puce--sourde" disabled={bloque || enCombat} onClick={surEteint}>
                {t("Étouffer")}
              </button>
            </span>
          </div>
        ) : null}

        <div className="pile pile--serree" style={{ marginTop: "var(--e3)" }}>
          <button type="button" className="bouton" disabled={bloque || enCombat || rations < 1} onClick={surRepas}>
            {t("Manger")} <span className="faible">{t("1 ration")}</span>
          </button>
          {Object.entries(REPOS).map(([type, mot]) => {
            // Le repos du sanctuaire n'apparaît QUE dans un sanctuaire : ailleurs, il n'existe pas.
            if (type === "sanctuaire" && !auSanctuaire) return null;
            // CE QUI MANQUE SE DIT, ET LE BOUTON RESTE LISIBLE (design §3 : désaturé d'un cran,
            // jamais éteint). Un « Halte » gris sans explication se lit comme une panne ; « il vous
            // faut 1 ration » se lit comme une règle, et l'on sait quoi aller chercher.
            const manque =
              type === "camp" && kits < 1
                ? t("kit de campement requis")
                : rations < RATIONS_DU_REPOS[type]!
                  ? tn(RATIONS_DU_REPOS[type]!, "il vous faut {n} ration", "il vous faut {n} rations")
                  : null;
            return (
              <button
                key={type}
                type="button"
                className="bouton"
                disabled={bloque || enCombat || manque !== null}
                onClick={() => surRepos(type)}
              >
                {mot.nom} <span className="faible">{manque ?? mot.detail}</span>
              </button>
            );
          })}
        </div>
        {/* LE REBOUTEUX (§3.4). Il n'apparaît que dans un sanctuaire ET que si l'on a quelque chose
            à refermer : une liste de blessures qu'on n'a pas serait un catalogue de malheurs
            possibles, ce qui n'est pas une information de jeu. Le prix vient du serveur au refus —
            on ne le recopie pas ici, parce qu'il monte avec l'acte. */}
        {auSanctuaire && heros.blessures.length > 0 ? (
          <div style={{ marginTop: "var(--e4)" }}>
            <p className="capitales" style={{ marginBottom: "var(--e2)" }}>{t("Le rebouteux")}</p>
            <p className="faible" style={{ marginTop: 0 }}>
              {t("Il referme une blessure, et une seule à la fois. Il faut la payer : {or} or en bourse.", {
                or: heros.or
              })}
            </p>
            <div className="pile pile--serree">
              {heros.blessures.map((blessure, i) => (
                <button
                  key={`${blessure}-${i}`}
                  type="button"
                  className="bouton"
                  disabled={bloque || enCombat}
                  onClick={() => surSoin(blessure)}
                >
                  {t("Faire soigner")} <span className="faible">{BLESSURES[blessure]?.nom ?? blessure}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* LES BÉNÉDICTIONS EN COURS (§3.7) : de combien, et pour combien de temps. Une
            caractéristique qui monte sans qu'on sache jusqu'à quand est une fiche à laquelle on ne
            peut plus se fier. */}
        {heros.benedictions.length > 0 ? (
          <div style={{ marginTop: "var(--e4)" }}>
            <p className="capitales" style={{ marginBottom: "var(--e2)" }}>{t("Ce qui vous porte")}</p>
            <ul className="sac">
              {heros.benedictions.map((b) => (
                <li key={b.objetId}>
                  <span>{b.nom}</span>
                  <span className="nombre faible">
                    {Object.entries(b.bonus)
                      .map(([stat, valeur]) => `${valeur! > 0 ? "+" : ""}${valeur} ${CARACTERISTIQUES[stat as Caracteristique]?.abrege ?? stat}`)
                      .join(" ")}{" "}
                    · {t("{heures} h", { heures: b.heuresRestantes })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {enCombat ? <p className="faible" style={{ marginBottom: 0 }}>{t("Pas pendant un combat.")}</p> : null}
      </Popin>

      <Popin ouvert={ouvert === "sac"} titre={t("Le sac")} glyphe={<Glyphe nom="sac" taille={16} />} surFermeture={ferme}>
        <div className="rang rang--espace">
          <span className="capitales">{t("Charge")}</span>
          <span className={estSurcharge(heros.surcharge) ? "nombre" : "nombre faible"} style={estSurcharge(heros.surcharge) ? { color: "var(--sang)" } : undefined}>
            {poidsDit(heros.poids)} / {poidsDit(heros.capacite)}
          </span>
        </div>
        <div className="rang rang--espace">
          <span className="capitales">{t("Emplacements")}</span>
          <span className="nombre faible">
            {heros.slots.occupes} / {heros.slots.disponibles}
          </span>
        </div>
        {estSurcharge(heros.surcharge) ? (
          <p className="mention mention--verrou" style={{ marginTop: "var(--e2)" }}>
            <Glyphe nom="poids" taille={14} /> {t("Surchargé : l'Agilité en souffre, et au-delà d'une moitié de plus, on ne bouge plus.")}
          </p>
        ) : null}

        <ul className="sac">
          {heros.sac.map((pile) => {
            const objet = catalogue.get(pile.objetId);
            const clef = objet ? clefPour(objet, heros) : null;
            return (
              <li key={pile.objetId}>
                <span style={{ color: objet ? RARETES[objet.rarete]?.teinte : undefined }}>
                  {nomDObjet(pile.objetId, catalogue)}
                  {pile.quantite > 1 ? <span className="nombre faible"> {t("×")}{pile.quantite}</span> : null}
                </span>
                <span className="rang rang--serre">
                  {/* BOIRE, MANGER, SE PANSER (§3.3). Le geste manquait tout entier : deux potions
                      de soin partent dans chaque paquetage, et il n'existait aucun moyen de s'en
                      servir hors d'un combat. Le bouton porte SON EFFET, comme les actions de
                      combat — on ne boit pas une potion qu'on ne sait pas lire. */}
                  {objet?.consommable && !enCombat ? (
                    <button
                      type="button"
                      className="puce"
                      disabled={bloque}
                      title={ditLeConsommable(objet.consommable)}
                      onClick={() => surUtilise(pile.objetId)}
                    >
                      {verbeDeConsommation(objet.consommable)}{" "}
                      <span className="faible">{ditLeConsommable(objet.consommable)}</span>
                    </button>
                  ) : null}
                  {/* LIRE (§3.7). Un parchemin agit sur la situation et non sur le corps : le
                      bouton porte son effet comme celui d'une potion, et le repli — qui ne se lit
                      qu'en combat — n'apparaît pas ici. Un bouton qui ne peut que refuser est un
                      bouton qui ment. */}
                  {objet?.parchemin && !enCombat && objet.parchemin.genre !== "repli" ? (
                    objet.parchemin.genre === "rebouteux" ? (
                      // LE REBOUTEUX DEMANDE UNE CIBLE. Sans blessure, on n'affiche rien plutôt
                      // qu'un bouton qui coûterait un parchemin pour un refus.
                      heros.blessures.map((blessure, i) => (
                        <button
                          key={`lit-${blessure}-${i}`}
                          type="button"
                          className="puce"
                          disabled={bloque}
                          title={t("Refermer : {blessure}", { blessure: BLESSURES[blessure]?.nom ?? blessure })}
                          onClick={() => surLit(pile.objetId, blessure)}
                        >
                          {t("Lire")}{" "}
                          <span className="faible">
                            {t("referme {blessure}", {
                              blessure: BLESSURES[blessure]?.nom.toLowerCase() ?? blessure
                            })}
                          </span>
                        </button>
                      ))
                    ) : (
                      <button
                        type="button"
                        className="puce"
                        disabled={bloque}
                        title={ditLeParchemin(objet.parchemin)}
                        onClick={() => surLit(pile.objetId)}
                      >
                        {t("Lire")} <span className="faible">{ditLeParchemin(objet.parchemin)}</span>
                      </button>
                    )
                  ) : null}
                  {/* ALLUMER (§3.8). Le bouton porte sa durée, comme une potion porte ses PV : on
                      n'entre pas dans une galerie sans savoir si l'on en a pour quatre heures ou
                      pour une. Il reste offert au grand jour — on allume AVANT d'entrer, et refuser
                      au motif qu'il fait clair serait refuser la seule prévoyance possible. */}
                  {objet?.lumiere && !enCombat ? (
                    <button
                      type="button"
                      className="puce"
                      disabled={bloque}
                      title={t("Éclaire {heures} heures de marche", { heures: objet.lumiere.heures })}
                      onClick={() => surAllume(pile.objetId)}
                    >
                      {t("Allumer")}{" "}
                      <span className="faible">{t("{heures} h de lumière", { heures: objet.lumiere.heures })}</span>
                    </button>
                  ) : null}
                  {/* UNE CLEF DIT CE QU'ELLE OUVRE (§3.6), et ne se presse pas : c'est une mention,
                      pas un geste. On l'ouvre en fouillant la serrure, pas en cliquant le sac. */}
                  {objet?.clef ? (
                    <span className="faible" title={t("Ouvre {quoi}", { quoi: objet.clef.ouvre })}>
                      {t("ouvre {quoi}", { quoi: objet.clef.ouvre })}
                    </span>
                  ) : null}
                  {objet?.outil?.boussole ? <span className="faible">{t("donne le cap sur la carte")}</span> : null}
                  {clef && !enCombat ? (
                    <button type="button" className="puce" disabled={bloque} onClick={() => surEquipe(pile.objetId, clef, false)}>
                      {t("Porter")}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="puce puce--sourde"
                    disabled={bloque || enCombat}
                    onClick={() => surJette(pile.objetId, 1)}
                    title={
                      objet?.contenant
                        ? t("Se défaire de ce contenant — vous perdrez les emplacements qu'il porte, et il est perdu")
                        : t("Se défaire de cet objet — il ne reste pas au sol, il est perdu")
                    }
                  >
                    {t("Jeter")}
                  </button>
                </span>
              </li>
            );
          })}
          {!heros.sac.length ? <li className="faible">{t("Rien.")}</li> : null}
        </ul>

        <div className="tiroir__soustitre capitales">{t("Porté")}</div>
        <Equipement heros={heros} catalogue={catalogue} />
        <div className="rang" style={{ marginTop: "var(--e3)", flexWrap: "wrap" }}>
          {Object.entries(heros.equipement)
            .filter(([, objetId]) => objetId)
            .map(([clef]) => (
              <button
                key={clef}
                type="button"
                className="puce puce--sourde"
                disabled={bloque || enCombat}
                onClick={() => surEquipe(null, clef, true)}
              >
                {t("Retirer {quoi}", { quoi: EMPLACEMENTS[clef] ?? clef })}
              </button>
            ))}
        </div>
      </Popin>

      {/* LA CARTE PREND TOUTE LA PLACE QU'ELLE PEUT. Une carte réduite à trois cents pixels dans une
          marge est une carte qu'on ne lit pas : on l'étudie, et pendant ce temps le texte n'a pas
          besoin d'être visible. */}
      <Popin ouvert={ouvert === "carte"} titre={t("La carte")} glyphe={<Glyphe nom="carte" taille={16} />} large surFermeture={ferme}>
        <LaCarteAnnotee
          carte={carte}
          annotations={annotations}
          frequentation={frequentation}
          surAnnote={surAnnote}
          surResout={surResout}
          surPublie={surPublie}
          surEfface={surEfface}
          surVote={surVote}
        />
      </Popin>

      <Popin ouvert={ouvert === "journal"} titre={t("Le journal")} glyphe={<Glyphe nom="plume" taille={16} />} surFermeture={ferme}>
        <LeCarnetDeQuetes quetes={partie.quetes} />
        {partie.quetes.length ? <div className="tiroir__soustitre capitales">{t("Ce qui s'est passé")}</div> : null}
        <ol className="journal">
          {journal.map((ligne, index) => (
            <li key={index}>
              <span className="journal__heure nombre">{dureeDite(ligne.heure)}</span>
              <span>{ditLaLigne(ligne)}</span>
            </li>
          ))}
          {!journal.length ? <li className="faible">{t("Rien d'écrit pour l'instant.")}</li> : null}
        </ol>
      </Popin>

      <Popin ouvert={ouvert === "pupitre"} titre={t("Le pupitre")} glyphe={<Glyphe nom="lampe" taille={16} />} surFermeture={ferme}>
        <LePupitre />
      </Popin>
    </>
  );
}
